/** Legacy catalog used only when migrating old Compliances widget configs (id[] + customItems). */
export const LEGACY_COMPLIANCE_CATALOG = [
    { id: "ccpa", short: "CCPA", name: "CCPA", body: "State of California", icon: "padlock-locked", group: "Privacy", desc: "Compliant with the California Consumer Privacy Act." },
    { id: "cpra", short: "CPRA", name: "CPRA", body: "State of California", icon: "padlock-locked", group: "Privacy", desc: "Compliant with the California Privacy Rights Act." },
    { id: "dpdp", short: "DPDP", name: "DPDP Act India", body: "Government of India", icon: "padlock-locked", group: "Privacy", desc: "Aligned to India's Digital Personal Data Protection Act." },
    { id: "npc", short: "NPC", name: "NPC", body: "Philippines", icon: "padlock-locked", group: "Privacy", desc: "Registered with the Philippines National Privacy Commission." },
    { id: "gdpr", short: "GDPR", name: "GDPR", body: "European Union", icon: "padlock-locked", group: "Privacy", desc: "Compliant with the EU General Data Protection Regulation as processor." },
    { id: "soc2", short: "SOC 2", name: "SOC 2 Type II", body: "AICPA", icon: "shield-check", group: "Audit", desc: "Independent audit of security, availability and confidentiality controls, reported annually." },
    { id: "soc1", short: "SOC 1", name: "SOC 1 Type II", body: "AICPA", icon: "shield-check", group: "Audit", desc: "Controls relevant to customers' financial reporting, audited annually." },
    { id: "payroll-soc1", short: "Payroll SOC 1", name: "Payroll SOC 1 Type II", body: "AICPA", icon: "shield-check", group: "Audit", desc: "SOC 1 Type II attestation covering payroll operations." },
    { id: "iso27001", short: "ISO 27001", name: "ISO 27001:2022", body: "ISO/IEC", icon: "ribbon-star", group: "ISO", desc: "Certified information security management system covering the full platform." },
    { id: "iso27701", short: "ISO 27701", name: "ISO 27701:2019", body: "ISO/IEC", icon: "ribbon-star", group: "ISO", desc: "Privacy information management extension to ISO 27001." },
    { id: "iso27017", short: "ISO 27017", name: "ISO 27017:2015", body: "ISO/IEC", icon: "ribbon-star", group: "ISO", desc: "Cloud-specific security controls for our infrastructure." },
    { id: "iso27018", short: "ISO 27018", name: "ISO 27018:2019", body: "ISO/IEC", icon: "ribbon-star", group: "ISO", desc: "Protection of personally identifiable information in the cloud." },
    { id: "iso9001", short: "ISO 9001", name: "ISO 9001:2015", body: "ISO", icon: "ribbon-star", group: "ISO", desc: "Certified quality management system." },
    { id: "mca", short: "MCA", name: "MCA — Audit Trail & Backup Regulatory Compliance", body: "Govt. of India", icon: "file-shield", group: "Regulatory", desc: "Meets India's MCA audit-trail and backup regulatory requirements." },
]

function slugify(text) {
    return String(text || "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, "")
}

const COMPLIANCE_STATUSES = new Set(["active", "pending", "in_progress", "inactive"])

/** Lower number = higher display priority: Active > Pending > Inactive. */
const STATUS_SORT_PRIORITY = {
    active: 0,
    pending: 1,
    in_progress: 2,
    inactive: 3,
}

export function normalizeComplianceStatus(status) {
    const value = String(status || "active").toLowerCase().replace(/\s+/g, "_")
    if (value === "in-progress") return "in_progress"
    return COMPLIANCE_STATUSES.has(value) ? value : "active"
}

export function compareComplianceByStatus(a, b) {
    const pa = STATUS_SORT_PRIORITY[normalizeComplianceStatus(a?.status)] ?? 99
    const pb = STATUS_SORT_PRIORITY[normalizeComplianceStatus(b?.status)] ?? 99
    return pa - pb
}

export function normalizeComplianceRecord(item) {
    if (!item || typeof item === "string") return null
    const id = item.id || slugify(item.short || item.name)
    if (!id) return null
    return {
        id,
        short: item.short || item.name || "",
        name: item.name || item.short || "",
        body: item.body || "",
        desc: item.desc || item.description || "",
        icon: item.icon || "ribbon-star",
        url: item.url || "",
        logoUrl: item.logoUrl || "",
        svg: item.svg || "",
        group: item.group || "Certification",
        status: normalizeComplianceStatus(item.status),
    }
}

export function isLegacyComplianceConfig(config) {
    const items = config?.items
    return Array.isArray(items) && items.length > 0 && typeof items[0] === "string"
}

export function migrateLegacyComplianceConfig(config) {
    const selectedIds = config?.items || []
    const customItems = config?.customItems || []
    const bodies = config?.bodies || {}
    const urls = config?.urls || {}
    const catalog = [...LEGACY_COMPLIANCE_CATALOG, ...customItems]
    const byId = Object.fromEntries(catalog.map((c) => [c.id, c]))

    return selectedIds
        .map((id) => {
            const base = byId[id]
            if (!base) return null
            return normalizeComplianceRecord({
                ...base,
                body: bodies[id] ?? base.body,
                url: urls[id] || "",
                desc: base.desc || base.description,
            })
        })
        .filter(Boolean)
}

export function normalizeComplianceItems(config) {
    if (!config?.items?.length) return []
    if (isLegacyComplianceConfig(config)) {
        return migrateLegacyComplianceConfig(config).sort(compareComplianceByStatus)
    }
    return config.items
        .map(normalizeComplianceRecord)
        .filter(Boolean)
        .sort(compareComplianceByStatus)
}

export function toComplianceWidgetItems(records) {
    return records.map((item) => ({
        id: item.id,
        short: item.short,
        name: item.name,
        body: item.body,
        desc: item.desc,
        icon: item.icon,
        url: item.url || "",
        logoUrl: item.logoUrl || "",
        svg: item.svg || "",
        group: item.group || "Certification",
        status: normalizeComplianceStatus(item.status),
    }))
}
